import { useLazyQuery, useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'

import {
  PURGE_DELETED_PROJECT_ASSETS,
  RECOVER_DELETED_PROJECT
} from 'data/layoutcreator/mutations/admin'
import { GET_ADMIN_PROJECTS } from 'data/layoutcreator/queries/admin'
import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import Input from 'shared/ui/Input'
import Text from 'shared/ui/Text'
import YearbookCard from 'shared/ui/YearbookCardNew'
import YearbookCardSkeleton from 'shared/ui/YearbookCardNew/Skeleton'
import { isGroupId } from 'shared/utils/gradoo'

const LAST_GROUP_ID_STORAGE_KEY = 'adminLastGroupId'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const ProjectsContainer = styled(Flex)`
  animation: ${fadeIn} 0.5s;
`

const ProjectsTab = () => {
  const { formatMessage } = useIntl()
  const lastGroupId = localStorage.getItem(LAST_GROUP_ID_STORAGE_KEY)
  const [groupId, setGroupId] = useState(lastGroupId || '')

  const [
    fetchProjects,
    {
      data: projectsData,
      error: projectsSearchError,
      loading: isProjectsLoading
    }
  ] = useLazyQuery(GET_ADMIN_PROJECTS)
  const [recoverDeletedProject] = useMutation(
    RECOVER_DELETED_PROJECT,
    {
      refetchQueries: [
        { query: GET_ADMIN_PROJECTS, variables: { groupId } }
      ]
    }
  )
  const [purgeDeletedProjectAssets] = useMutation(
    PURGE_DELETED_PROJECT_ASSETS
  )

  useEffect(() => {
    toast.remove()

    if (!isGroupId(groupId)) {
      toast.error(
        formatMessage({ id: 'Toasts.searchByGroupId.error' })
      )
      return
    }

    localStorage.setItem(LAST_GROUP_ID_STORAGE_KEY, groupId)

    fetchProjects({ variables: { groupId } })
  }, [groupId])

  const handlePurgeAssets = async (id: string) => {
    if (
      !window.confirm(
        'This will permanently delete customer images and page backgrounds for this project from Chili. This cannot be undone. Continue?'
      )
    ) {
      return
    }

    const toastId = toast.loading('Purging project assets...')

    try {
      await purgeDeletedProjectAssets({
        variables: { projectId: id, groupId }
      })
      toast.success('Project assets purged successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to purge project assets')
    }

    toast.dismiss(toastId)
  }

  const handleRecoverProject = async (id: string) => {
    const firstToast = toast.loading(
      formatMessage({ id: 'Toasts.recoverProject.loading' }),
      {
        icon: '🔧'
      }
    )

    try {
      await recoverDeletedProject({
        variables: { groupId, projectId: id }
      })
      toast.success(
        formatMessage({ id: 'Toasts.recoverProject.success' })
      )
    } catch (error) {
      toast.error(
        formatMessage({ id: 'Toasts.recoverProject.error' })
      )
    }

    toast.dismiss(firstToast)
  }

  return (
    <Flex direction="column">
      <Flex
        direction="column"
        marginTop={48}
        marginBottom={16}
        rowGap={16}
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Text
            tag="h1"
            id="Admin.projects.title"
            size={28}
            weight={600}
            marginBottom={16}
          />
        </Flex>

        <Input
          width={600}
          label={formatMessage({
            id: 'Admin.projects.groupId.label'
          })}
          value={groupId}
          onChange={setGroupId}
          withBackground
        />
      </Flex>

      <Flex
        justifyContent="space-between"
        alignItems="flex-end"
        marginBottom={16}
      >
        <Text
          tag="h2"
          id="Admin.projects.results"
          size={16}
          weight={600}
          marginTop={100}
        />
      </Flex>

      <ProjectsContainer
        wrap="wrap"
        columnGap={16}
        rowGap={24}
        alignItems="flex-start"
        marginBottom={128}
      >
        {projectsData?.getAdminProjects.length === 0 &&
          formatMessage({ id: 'Admin.error.noProjectsFound' })}
        {projectsSearchError &&
          formatMessage({ id: 'Admin.error.errorSearchingGroup' })}
        {isProjectsLoading &&
          new Array(8).fill(null).map(() => <YearbookCardSkeleton />)}

        {projectsData?.getAdminProjects.map((project, i) => (
          <YearbookCard
            key={i}
            name={project.name}
            isDeleted={!!project.deleted}
            linkTo={
              project.id && !project.deleted
                ? `/admin/projects/${groupId}/${project.id}`
                : null
            }
            pagePreviews={project.pagePreviews}
            append={() => (
              <Flex direction="column" rowGap={8}>
                <Text
                  tag="span"
                  text={
                    project.id ||
                    formatMessage({ id: 'Admin.message.missingMeta' })
                  }
                  size={10}
                  color={theme =>
                    !!project.id
                      ? theme.color.base.c6
                      : theme.color.error
                  }
                />

                {!!project.deleted && (
                  <Button
                    textId="Admin.buttons.recoverProject"
                    onPress={() => handleRecoverProject(project.id!)}
                    intent="secondary-contrast"
                  />
                )}

                {!!project.deleted && (
                  <Button
                    textId="Admin.buttons.purgeAssets"
                    onPress={() => handlePurgeAssets(project.id!)}
                    intent="destructive"
                  />
                )}
              </Flex>
            )}
          />
        ))}
      </ProjectsContainer>
    </Flex>
  )
}

export default ProjectsTab
